import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "@emotion/styled";
import { motion } from "framer-motion";
import { Form, FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { Box, Container, Stack, TextField, Typography, Alert, Button, CircularProgress, Autocomplete } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Links } from "utils/pageLinkNames";
import { fetchDepartments, registerUser } from "features/auth/registrationApi";
import { CustomTextBox } from "component/ui/CustomTextbox";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { isValidPhoneNumber } from "libphonenumber-js";

const CustomPhoneInput = styled(PhoneInput)(({ theme }) => ({
  "& .PhoneInputInput": {
    height: "56px",
    padding: "16.5px 14px",
    borderWidth: "1px",
    borderStyle: "solid",
    borderRadius: "4px",
    borderColor: "rgba(0, 0, 0, 0.23)",
    width: "100%",
    fontSize: "16px",
    backgroundColor: "#ffffff",
    color: "black",
    "&:focus": {
      borderColor: "#1976d2",
      borderWidth: "2px !important",
      borderStyle: "solid !important",
      outline: "none",
    },
  },
  "& .PhoneInputCountryIcon": {
    height: "20px",
    width: "28px",
  },
}));

const RootStyle = styled("div")({
  background: "rgb(249, 250, 251)",
  minHeight: "100vh",
  display: "grid",
  placeItems: "center",
});

const ContentStyle = styled("div")({
  width: "100%",
  maxWidth: 560,
  padding: 25,
  margin: "auto",
  display: "flex",
  justifyContent: "center",
  flexDirection: "column",
  background: "#fff",
  boxShadow: "rgba(0, 0, 0, 0.3) 0px 19px 38px, rgba(0, 0, 0, 0.22) 0px 15px 12px",
});

let easing = [0.6, -0.05, 0.01, 0.99];
const fadeInUp = {
  initial: { y: 40, opacity: 0, transition: { duration: 0.6, ease: easing } },
  animate: { y: 0, opacity: 1, transition: { duration: 0.6, ease: easing } },
};

const RECAPTCHA_SITE_KEY = "6LfK1eIsAAAAAH0o1GgrAjC0O0HXG7E8PqmYfc18";
const RECAPTCHA_ACTION = "CREATE_USER_REGISTRATION";
const RECAPTCHA_SCRIPT_ID = "google-recaptcha-enterprise";
const RECAPTCHA_SCRIPT_SRC = "https://www.google.com/recaptcha/enterprise.js?render=explicit";

const CreateUser = () => {
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState("");
  const [departments, setDepartments] = useState([]);
  const [departmentsLoading, setDepartmentsLoading] = useState(false);
  const [departmentsError, setDepartmentsError] = useState("");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaError, setCaptchaError] = useState("");
  const [captchaReady, setCaptchaReady] = useState(false);
  const [captchaLoadError, setCaptchaLoadError] = useState("");
  const recaptchaContainerRef = useRef(null);
  const recaptchaWidgetIdRef = useRef(null);

  const resetRecaptcha = ({ updateState = true } = {}) => {
    const grecaptcha = window.grecaptcha?.enterprise;
    if (grecaptcha && recaptchaWidgetIdRef.current !== null) {
      grecaptcha.reset(recaptchaWidgetIdRef.current);
    }
    if (updateState) {
      setCaptchaToken("");
    }
  };

  const cleanupRecaptchaArtifacts = () => {
    resetRecaptcha({ updateState: false });
    recaptchaWidgetIdRef.current = null;

    if (recaptchaContainerRef.current) {
      recaptchaContainerRef.current.innerHTML = "";
    }

    const removeIfDetached = (node) => {
      let current = node?.parentElement;
      while (current && current !== document.body) {
        if (current.childElementCount > 0 || (current.textContent || "").trim()) {
          break;
        }
        const parent = current.parentElement;
        current.remove();
        current = parent;
      }
    };

    document
      .querySelectorAll('iframe[src*="recaptcha"], iframe[title*="recaptcha"], textarea[name^="g-recaptcha-response"]')
      .forEach((node) => {
        const parent = node.parentElement;
        node.remove();
        removeIfDetached(parent);
      });
  };

  useEffect(() => {
    let mounted = true;
    (async () => {
      setDepartmentsError("");
      setDepartmentsLoading(true);
      try {
        const list = await fetchDepartments();
        if (!mounted) return;
        const normalized = (list || [])
          .map((d) => ({
            id: d?.id,
            name: d?.name,
          }))
          .filter((d) => d?.id !== null && d?.id !== undefined && String(d?.name || "").trim() !== "");

        normalized.sort((a, b) => String(a.name).localeCompare(String(b.name)));
        setDepartments(normalized);
      } catch (e) {
        if (!mounted) return;
        setDepartments([]);
        setDepartmentsError("Unable to load departments. Please try again later.");
      } finally {
        if (!mounted) return;
        setDepartmentsLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  useEffect(() => {
    if (window.grecaptcha?.enterprise) {
      setCaptchaReady(true);
      setCaptchaLoadError("");
      return undefined;
    }

    const existingScript = document.getElementById(RECAPTCHA_SCRIPT_ID);
    const script = existingScript || document.createElement("script");

    const handleLoad = () => {
      setCaptchaReady(true);
      setCaptchaLoadError("");
    };

    const handleError = () => {
      setCaptchaLoadError("reCAPTCHA is temporarily unavailable. Please try again later.");
      setCaptchaReady(false);
    };

    if (!existingScript) {
      script.id = RECAPTCHA_SCRIPT_ID;
      script.src = RECAPTCHA_SCRIPT_SRC;
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }

    script.addEventListener("load", handleLoad);
    script.addEventListener("error", handleError);

    return () => {
      script.removeEventListener("load", handleLoad);
      script.removeEventListener("error", handleError);
    };
  }, []);

  useEffect(() => {
    return () => {
      cleanupRecaptchaArtifacts();
    };
  }, []);

  useEffect(() => {
    if (!captchaReady || !recaptchaContainerRef.current || recaptchaWidgetIdRef.current !== null) {
      return undefined;
    }

    const grecaptcha = window.grecaptcha?.enterprise;
    if (!grecaptcha) {
      return undefined;
    }

    let cancelled = false;
    grecaptcha.ready(() => {
      if (cancelled || !recaptchaContainerRef.current || recaptchaWidgetIdRef.current !== null) {
        return;
      }

      recaptchaWidgetIdRef.current = grecaptcha.render(recaptchaContainerRef.current, {
        sitekey: RECAPTCHA_SITE_KEY,
        action: RECAPTCHA_ACTION,
        callback: (token) => {
          setCaptchaToken(token || "");
          setCaptchaError("");
          setSubmitError("");
        },
        "expired-callback": () => {
          setCaptchaToken("");
        },
        "error-callback": () => {
          resetRecaptcha();
          setCaptchaError("reCAPTCHA could not be verified. Please try again.");
        },
      });
    });

    return () => {
      cancelled = true;
    };
  }, [captchaReady]);

  const schema = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string().trim().required("Name is required"),
        employeeId: Yup.string().trim().required("Employee ID is required"),
        mobileNumber: Yup.string()
          .nullable()
          .required("Mobile Number is required")
          .test("isValidPhone", "Please enter a valid mobile number", (value) =>
            value ? isValidPhoneNumber(value) : false
          ),
        email: Yup.string().trim().email("Provide a valid email address").nullable().notRequired(),
        departmentId: Yup.number()
          .typeError("Department is required")
          .required("Department is required"),
      }),
    []
  );

  const formik = useFormik({
    initialValues: {
      name: "",
      employeeId: "",
      mobileNumber: "",
      email: "",
      departmentId: "",
    },
    validationSchema: schema,
    onSubmit: async (values, helpers) => {
      setSubmitError("");
      setCaptchaError("");

      if (!captchaToken) {
        setSubmitError("Please complete the reCAPTCHA challenge.");
        return;
      }

      try {
        const payload = {
          name: values.name.trim(),
          employeeId: values.employeeId.trim(),
          mobileNumber: values.mobileNumber,
          departmentId: Number(values.departmentId),
          recaptchaToken: captchaToken,
          ...(values.email ? { email: values.email.trim() } : {}),
        };

        const data = await registerUser(payload);
        toast.success(data?.message || "Registration request submitted");
        helpers.resetForm();
        resetRecaptcha();
        navigate(`/${Links.LOGIN}`, { replace: true });
      } catch (e) {
        const backend = e?.response?.data || e;
        const backendMessage =
          typeof backend?.message === "string" && backend.message.trim() ? backend.message.trim() : "";

        resetRecaptcha();

        if (backend?.error === "Duplicate registration request") {
          const duplicateField = backend?.duplicateField;
          if (duplicateField === "employeeId" || duplicateField === "mobileNumber") {
            formik.setFieldError(duplicateField, backendMessage || `Duplicate ${duplicateField}`);
            formik.setFieldTouched(duplicateField, true);
          }
          setSubmitError(backendMessage || "Duplicate registration request");
          return;
        }

        if (
          backend?.code === "RECAPTCHA_REQUIRED" ||
          backend?.code === "RECAPTCHA_INVALID" ||
          backend?.code === "RECAPTCHA_ACTION_MISMATCH"
        ) {
          setSubmitError(backendMessage || "Please complete the reCAPTCHA challenge again.");
          return;
        }

        setSubmitError(backendMessage || "Something went wrong. Please try again.");
      }
    },
  });

  const { errors, touched, isSubmitting, handleSubmit, getFieldProps } = formik;
  const selectedDepartment =
    departments.find((d) => String(d.id) === String(formik.values.departmentId)) || null;

  return (
    <RootStyle>
      <Container maxWidth="sm">
        <ContentStyle>
          <Box component={motion.div} {...fadeInUp}>
            <Stack spacing={1} sx={{ mb: 3, textAlign: "center" }}>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                New User Registration
              </Typography>
            </Stack>
          </Box>

          <FormikProvider value={formik}>
            <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <CustomTextBox
                  label="Name*"
                  name="name"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={Boolean(touched.name && errors.name)}
                  helperText={touched.name && errors.name}
                />
                <CustomTextBox
                  label="Employee ID*"
                  name="employeeId"
                  casing="none"
                  value={formik.values.employeeId}
                  onChange={(e) => {
                    const next = String(e?.target?.value ?? "").replace(/[^a-zA-Z0-9]/g, "");
                    formik.setFieldValue("employeeId", next);
                  }}
                  onBlur={formik.handleBlur}
                  error={Boolean(touched.employeeId && errors.employeeId)}
                  helperText={touched.employeeId && errors.employeeId}
                />

                <Autocomplete
                  options={departments}
                  value={selectedDepartment}
                  loading={departmentsLoading}
                  disabled={departmentsLoading || !departments.length}
                  isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
                  getOptionLabel={(option) => option?.name ?? ""}
                  onChange={(_e, newValue) => {
                    formik.setFieldValue("departmentId", newValue?.id ?? "");
                  }}
                  onBlur={() => formik.setFieldTouched("departmentId", true)}
                  ListboxProps={{
                    sx: {
                      maxHeight: 300,
                    },
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Department*"
                      error={Boolean(touched.departmentId && errors.departmentId)}
                      helperText={touched.departmentId && errors.departmentId}
                      inputProps={{
                        ...params.inputProps,
                        style: { color: "black" },
                      }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {departmentsLoading ? <CircularProgress color="inherit" size={18} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      }}
                    />
                  )}
                />

                {departmentsLoading ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CircularProgress size={16} />
                    <Typography variant="body2">Loading departments…</Typography>
                  </Stack>
                ) : null}

                {departmentsError ? <Alert severity="error">{departmentsError}</Alert> : null}

                <Box>
                  <CustomPhoneInput
                    international={true}
                    defaultCountry="SA"
                    value={formik.values.mobileNumber || ""}
                    onChange={(value) => formik.setFieldValue("mobileNumber", value || "")}
                    countryCallingCodeEditable={false}
                    withCountryCallingCode={false}
                  />
                  {touched.mobileNumber && errors.mobileNumber ? (
                    <Typography color="error" sx={{ fontSize: "0.75rem", mt: 0.75, ml: 1.75 }}>
                      {errors.mobileNumber}
                    </Typography>
                  ) : null}
                </Box>
                <CustomTextBox
                  label="Email"
                  name="email"
                  type="email"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={Boolean(touched.email && errors.email)}
                  helperText={touched.email && errors.email}
                />

                {submitError ? <Alert severity="error">{submitError}</Alert> : null}

                <Box>
                  <Box
                    sx={{
                      minHeight: 78,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <div ref={recaptchaContainerRef} />
                  </Box>
                  {captchaLoadError ? <Alert severity="error">{captchaLoadError}</Alert> : null}
                </Box>

                <LoadingButton
                  fullWidth
                  size="large"
                  type="submit"
                  variant="contained"
                  loading={isSubmitting}
                  disabled={Boolean(captchaLoadError) || !captchaReady || !captchaToken}
                  sx={{ color: "white", fontWeight: "bold" }}
                >
                  {isSubmitting ? "Submitting..." : "Submit"}
                </LoadingButton>

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Button
                    variant="text"
                    component={RouterLink}
                    to={`/${Links.LOGIN}`}
                    replace
                    sx={{ textTransform: "none" }}
                  >
                    Back to login
                  </Button>

                  <Button
                    variant="text"
                    onClick={() => {
                      formik.resetForm();
                      setSubmitError("");
                      setCaptchaError("");
                      resetRecaptcha();
                    }}
                  >
                    Clear
                  </Button>
                </Stack>
              </Stack>
            </Form>
          </FormikProvider>
        </ContentStyle>
      </Container>
    </RootStyle>
  );
};

export default CreateUser;

